/**
 * echarts-builder.ts — Plan 05-12 Task 1.
 *
 * Canonical ECharts option builders for the 4 chart shapes used in
 * MainBucketedTile + MainTrendTile.
 *
 * Chart category palette (Stack-0..5) from UI-SPEC:
 *   Stack-0: --color-brand-700 = #1f3fb4
 *   Stack-1: --color-brand-600 = #2851e6
 *   Stack-2: --color-brand-500 = #3461ff
 *   Stack-3: --color-adeptia-cyan = #3ccbf8
 *   Stack-4: --color-ink-500 = #64748b
 *   Stack-5: --color-ink-700 = #334155
 */
import type { EChartsOption } from 'echarts';

// Chart category palette per UI-SPEC §"Chart category palette"
export const CHART_STACK_COLORS = [
  '#1f3fb4', // Stack-0 brand-700
  '#2851e6', // Stack-1 brand-600
  '#3461ff', // Stack-2 brand-500
  '#3ccbf8', // Stack-3 adeptia-cyan
  '#64748b', // Stack-4 ink-500
  '#334155', // Stack-5 ink-700
];

// Brand blue for sparklines
const SPARKLINE_COLOR = '#2851e6';

/**
 * X-axis tick formatting mode for sparklines.
 *   - 'hidden'  : no X-axis (legacy default — '-Nd' relative day labels off)
 *   - 'month'   : month abbreviations (e.g. "Jun") from snap dates
 *   - 'quarter' : quarter labels (e.g. "Q2 FY26") from snap dates
 *   - 'date'    : the SNAPSHOT DATE each point was measured, 'M/D' (e.g. "6/25")
 *                 — quick-260625-nem Task 3. The metric is one daily point-in-time
 *                 value, so the axis is the measurement date, not a sub-period.
 *                 Replaces the hz9 month/quarter ticks for MAIN-13..18.
 */
export type SparklineAxisMode = 'hidden' | 'month' | 'quarter' | 'date';

export interface SparklineAxisOptions {
  /** ISO snap dates aligned 1:1 with the data values (oldest → newest). */
  dates?: string[];
  /** Tick label mode. Defaults to 'hidden' (legacy behavior). */
  axisMode?: SparklineAxisMode;
  /** Optional X-axis title (e.g. "pipeline over time"). */
  xAxisName?: string;
  /**
   * Optional per-point sample sizes (n) aligned 1:1 with the values
   * (quick-260625-nem Task 5). When present, the sparkline enables a tooltip
   * that annotates each point with "n = <count>" so cohorts driven by a small
   * sample (or a single outlier) are visible to the reader. The value unit
   * label (e.g. "hrs") is set via `valueSuffix`.
   */
  counts?: (number | null)[];
  /** Tooltip value suffix, e.g. 'hrs' (used with `counts`). */
  valueSuffix?: string;
  /**
   * When set, render a visible Y-axis with this title (quick-260625-nem Task 6).
   * e.g. MAIN-21 shows a "stalled deals" Y-axis with numeric ticks.
   * Defaults to hidden Y-axis (the dense-sparkline default).
   */
  yAxisName?: string;
  /**
   * quick-260630-f7f Task 3 (items 7/20): format the visible Y-axis ticks as
   * compact currency ($Nk / $N.NM) instead of plain numbers. Used by the Open
   * Mature Pipeline tiles (MAIN-14/16/18) so the $ axis reads in money.
   */
  yAxisCurrency?: boolean;
  /**
   * quick-260630-f7f Task 3 (item 9): pin the visible Y-axis to a fixed [min,max]
   * range — e.g. a percent tile uses [0,1] (stored as a 0–1 ratio) so a drop
   * from 80% → 50% reads against the full 0–100 scale rather than auto-zooming
   * flat. When omitted the axis uses `scale: true` (data-band autoscale, item 7).
   */
  yMin?: number;
  yMax?: number;
  /**
   * quick-260630-f7f Task 3 (item 9): percent Y-axis — render tick labels as
   * "NN%" (the stored value is a 0–1 ratio). Pairs with yMin/yMax = 0/1.
   */
  yAxisPercent?: boolean;
}

/**
 * Y-axis value unit for the lineByQuarter chart (quick-260625-nem Tasks 2/4).
 * Drives the Y-axis tick + tooltip formatting so each quarter's dot reads in
 * the metric's native unit: 'days' (MAIN-20 Sales Cycle) or 'currency'
 * (MAIN-12 Avg $$/Opp). 'number' is the plain default.
 */
export type QuarterValueUnit = 'number' | 'days' | 'currency' | 'percent';

export interface LineByQuarterOptions {
  /** Y-axis + tooltip value unit. Defaults to 'number'. */
  valueUnit?: QuarterValueUnit;
  /** Y-axis title (e.g. "Avg cycle (days)" / "Avg $ / opp"). */
  yAxisName?: string;
}

/**
 * Value unit for the horizontal-bar chart (quick-260626-cot Task 3.3).
 *   - 'percent'  : bucket value is a 0–1 ratio rendered "31%"
 *   - 'days'     : "243 days"
 *   - 'currency' : "$1.2k" / "$3M"
 *   - 'number'   : plain count
 */
export type BarValueUnit = 'percent' | 'days' | 'currency' | 'number';

export interface HorizontalBarOptions {
  /** Bar value + axis-label unit. Defaults to 'number'. */
  valueUnit?: BarValueUnit;
}

/**
 * Input for the cumulative-vs-target PACING chart (quick-260626-cot Task 3.2).
 *   - months: X-axis labels in fiscal order (e.g. ['Jun','Jul',...,'May'])
 *   - cumulative: cumulative actual $ aligned to `months` up to "now"
 *     (length may be < months.length; remaining months render no actual point)
 *   - target: FY coverage target — the straight 0 → target reference line
 *     spans the full month span.
 */
export interface PacingInput {
  months: string[];
  cumulative: number[];
  target: number;
}

/**
 * Build a sparkline ECharts option (line chart with area fill).
 * Used by MainTrendTile. Mirrors CroTrendTile's buildSparklineOption.
 *
 * @param data  - Array of snapshot values (oldest → newest)
 * @param target - Optional target value for a horizontal markLine
 * @param axis  - Optional X-axis tick formatting (month/quarter from snap dates)
 */
export function buildEchartsOption(
  type: 'sparkline',
  data: number[],
  target?: number,
  axis?: SparklineAxisOptions,
): EChartsOption;

/**
 * Build a stacked-bar ECharts option for MainBucketedTile.
 *
 * @param type - 'stackedBar'
 * @param data - Object: { categories: string[]; series: { name: string; values: number[] }[] }
 */
export function buildEchartsOption(
  type: 'stackedBar',
  data: { categories: string[]; series: { name: string; values: number[] }[] },
  target?: number,
): EChartsOption;

/**
 * Build an area ECharts option (line with fill) for MainBucketedTile.
 * Used for Mkt-Sourced Pipeline by Channel (MAIN-32).
 */
export function buildEchartsOption(
  // eslint-disable-next-line @typescript-eslint/unified-signatures -- separate overloads document the per-chart-type data shape
  type: 'area',
  data: { categories: string[]; series: { name: string; values: number[] }[] },
  target?: number,
): EChartsOption;

/**
 * Build a line-by-quarter ECharts option for MainBucketedTile. Each category is
 * a fiscal quarter; each dot is that quarter's value (avg days / avg $).
 *
 * quick-260630-icb Task 3: series values may include `null` so a line can GAP at
 * quarters with no data (e.g. the MAIN-12 closed-won line where a quarter has no
 * closed-won deals, while the point-in-time open line still draws a point).
 */
export function buildEchartsOption(
  type: 'lineByQuarter',
  data: { categories: string[]; series: { name: string; values: (number | null)[] }[] },
  target?: number,
  axis?: LineByQuarterOptions,
): EChartsOption;

/**
 * Build a horizontal-bar ECharts option (quick-260626-cot Task 3.3). One bar
 * per category, sorted desc by the caller, value labels formatted per
 * `opts.valueUnit` ('percent' / 'days' / 'currency' / 'number'). Used for the
 * by-channel analytical tiles MAIN-34/35/36/42/43.
 */
export function buildEchartsOption(
  type: 'horizontalBar',
  data: { categories: string[]; series: { name: string; values: number[] }[] },
  target?: number,
  axis?: HorizontalBarOptions,
): EChartsOption;

/**
 * Build a vertical date-axis bar chart (quick-260626-g28 Task 2). X = activity
 * day label ('M/D'); Y = completed-activity count. Thin bars with weekend gaps
 * naturally visible as low/zero columns. Used for the daily Email/Call/LinkedIn
 * activity tiles (MAIN-05/06/07).
 */
export function buildEchartsOption(
  type: 'dailyBar',
  data: { categories: string[]; series: { name: string; values: number[] }[] },
): EChartsOption;

/**
 * Build a vertical calendar-week bar chart (quick-260626-j5u Task 3). Same shape
 * as `dailyBar` (X = week-start 'M/D', Y = count) but ~5 wider bars over the
 * trailing 30 days. Used for the weekly Outbound/Email/Call/LinkedIn activity
 * tiles (MAIN-04/05/06/07).
 *
 * quick-260630-f7f Task 1 (item 3): an optional `target` draws a horizontal
 * dashed markLine (e.g. MAIN-01 New Meetings Booked at 120/week — quick-260630-wiw)
 * so the weekly "fill-up the bar" tile reads against its weekly goal.
 */
export function buildEchartsOption(
  // eslint-disable-next-line @typescript-eslint/unified-signatures -- separate overloads document the per-chart-type data shape
  type: 'weeklyBar',
  data: { categories: string[]; series: { name: string; values: number[] }[] },
  target?: number,
): EChartsOption;

/**
 * Build a cumulative-vs-target PACING chart (quick-260626-cot Task 3.2). Renders
 * the cumulative actual line (cyan), a straight 0 → target reference line
 * (dashed), and an amber shaded band between actual and target while the actual
 * sits below the target. Used by MainPacingTile (MAIN-17 headline hero).
 */
export function buildEchartsOption(
  type: 'pacing',
  data: PacingInput,
): EChartsOption;

// Implementation overload
export function buildEchartsOption(
  type:
    | 'sparkline'
    | 'stackedBar'
    | 'area'
    | 'lineByQuarter'
    | 'horizontalBar'
    | 'dailyBar'
    | 'weeklyBar'
    | 'pacing',
  data:
    | number[]
    | PacingInput
    // quick-260630-icb Task 3: lineByQuarter series values may include `null`
    // (line gaps); widen the shared implementation union accordingly.
    | { categories: string[]; series: { name: string; values: (number | null)[] }[] },
  target?: number,
  axis?: SparklineAxisOptions | LineByQuarterOptions | HorizontalBarOptions,
): EChartsOption {
  switch (type) {
    case 'pacing':
      return buildPacing(data as PacingInput);
    case 'dailyBar':
    case 'weeklyBar':
      // Weekly is the same vertical count-bar shape as daily — fewer, wider bars.
      // quick-260630-f7f Task 1 (item 3): the weekly path forwards an optional
      // `target` so MAIN-01 can draw a horizontal goal markLine (120/week —
      // quick-260630-wiw).
      return buildDailyBar(
        data as { categories: string[]; series: { name: string; values: number[] }[] },
        type === 'weeklyBar' ? target : undefined,
      );
    case 'sparkline':
      return buildSparkline(data as number[], target, axis as SparklineAxisOptions | undefined);
    case 'stackedBar':
      return buildStackedBar(
        data as { categories: string[]; series: { name: string; values: number[] }[] },
      );
    case 'area':
      return buildArea(
        data as { categories: string[]; series: { name: string; values: number[] }[] },
      );
    case 'lineByQuarter':
      return buildLineByQuarter(
        data as { categories: string[]; series: { name: string; values: (number | null)[] }[] },
        axis,
      );
    case 'horizontalBar':
      return buildHorizontalBar(
        data as { categories: string[]; series: { name: string; values: number[] }[] },
        axis as HorizontalBarOptions | undefined,
      );
  }
}

// ─── Private builder functions ────────────────────────────────────────────────

const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Format an ISO date as a month abbreviation, e.g. '2026-06-12' → 'Jun'. */
function monthLabel(isoDate: string): string {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  return MONTH_ABBR[d.getUTCMonth()] ?? '';
}

/** Format an ISO date as a quarter label, e.g. '2026-04-01' → 'Q2 FY26'. */
function quarterLabel(isoDate: string): string {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  const q = Math.floor(d.getUTCMonth() / 3) + 1;
  const yy = String(d.getUTCFullYear()).slice(-2);
  return `Q${String(q)} FY${yy}`;
}

/**
 * Format an ISO date as a short snapshot-date label 'M/D', e.g. '2026-06-25'
 * → '6/25' (quick-260625-nem Task 3). This is the date each daily snapshot was
 * measured — the X-axis for the pipeline trend tiles.
 */
function dateLabel(isoDate: string): string {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getUTCMonth() + 1)}/${String(d.getUTCDate())}`;
}

function buildSparkline(
  dataPoints: number[],
  target?: number,
  axis?: SparklineAxisOptions,
): EChartsOption {
  const axisMode = axis?.axisMode ?? 'hidden';
  const dates = axis?.dates;
  const counts = axis?.counts;
  const valueSuffix = axis?.valueSuffix;
  const yAxisName = axis?.yAxisName;
  // quick-260630-f7f Task 3: Y-axis legibility controls (items 7/9/20).
  const yAxisCurrency = axis?.yAxisCurrency ?? false;
  const yAxisPercent = axis?.yAxisPercent ?? false;
  const yMin = axis?.yMin;
  const yMax = axis?.yMax;

  // Default (legacy): relative '-Nd' weekly labels, axis hidden.
  let labels: string[] = dataPoints.map((_, i) => {
    const daysBack = (dataPoints.length - 1 - i) * 7;
    return daysBack === 0 ? 'Now' : `-${String(daysBack)}d`;
  });

  // Tick labels derived from snap dates. quick-260625-nem Task 3 adds 'date'
  // (the snapshot date, 'M/D') and reverts MAIN-13..18 to it; 'month'/'quarter'
  // (hz9 Task 6) remain available.
  const showAxis = axisMode !== 'hidden' && dates?.length === dataPoints.length;
  if (showAxis) {
    const fmt =
      axisMode === 'quarter' ? quarterLabel : axisMode === 'date' ? dateLabel : monthLabel;
    labels = dates.map(fmt);
  }

  interface MarkLineData {
    markLine: {
      silent: boolean;
      symbol: string;
      lineStyle: { color: string; width: number; type: 'dashed' };
      data: { yAxis: number; name: string }[];
      label: { show: boolean; position: 'insideEndTop'; formatter: string; fontSize: number; color: string };
    };
  }

  const markLineExt: MarkLineData | Record<string, never> =
    target != null
      ? {
          markLine: {
            silent: true,
            symbol: 'none',
            lineStyle: { color: '#d97706', width: 1, type: 'dashed' },
            data: [{ yAxis: target, name: 'target' }],
            label: {
              show: true,
              position: 'insideEndTop',
              formatter: 'Target',
              fontSize: 9,
              color: '#d97706',
            },
          },
        }
      : {};

  // For month/quarter ticks, collapse consecutive duplicate labels (e.g. several
  // weekly snaps in the same month) so the axis reads "Jun Jul Aug" not "Jun Jun
  // Jun". For 'date' mode every snapshot is a distinct measurement date, so keep
  // each label (ECharts' interval/hideOverlap thins them visually instead).
  const dedupeLabels = showAxis && axisMode !== 'date';
  const dedupedLabels = dedupeLabels
    ? labels.map((lbl, i) => (i > 0 && labels[i - 1] === lbl ? '' : lbl))
    : labels;

  const xAxisName = axis?.xAxisName;
  const hasAxisName = showAxis && xAxisName != null && xAxisName !== '';

  const xAxisNameExt = hasAxisName
    ? {
        name: xAxisName,
        nameLocation: 'middle' as const,
        nameGap: 24,
        nameTextStyle: { fontSize: 9, color: '#6b7280' },
      }
    : {};

  // quick-260625-nem Task 6: optional visible Y-axis with a title (MAIN-21).
  // quick-260630-f7f Task 3: also shown when caller opts into a currency axis
  // (items 20) or a fixed-range percent axis (item 9) even without a title.
  const showYAxis =
    (yAxisName != null && yAxisName !== '') || yAxisCurrency || yAxisPercent;
  // Tick label formatter: compact currency (item 20), "NN%" percent (item 9), or
  // the plain default. Stored percent values are 0–1 ratios.
  const yTickFormatter = yAxisCurrency
    ? (v: number): string => currencyTick(v)
    : yAxisPercent
      ? (v: number): string => `${String(Math.round(v * 100))}%`
      : undefined;
  // Fixed [min,max] (item 9) wins over data-band autoscale (`scale: true`, item 7).
  const hasFixedRange = yMin != null && yMax != null;
  const rangeExt = hasFixedRange ? { min: yMin, max: yMax } : { scale: true };
  const nameExt =
    yAxisName != null && yAxisName !== ''
      ? {
          name: yAxisName,
          nameLocation: 'middle' as const,
          nameGap: 28,
          nameTextStyle: { fontSize: 9, color: '#6b7280' },
        }
      : {};
  const yAxisOpt = showYAxis
    ? {
        type: 'value' as const,
        show: true,
        ...rangeExt,
        ...nameExt,
        axisLabel: {
          fontSize: 9,
          color: '#6b7280',
          ...(yTickFormatter ? { formatter: yTickFormatter } : {}),
        },
        splitLine: { show: true, lineStyle: { color: '#f3f4f6' } },
      }
    : { type: 'value' as const, show: false, scale: true };

  return {
    animation: false,
    grid: showYAxis
      ? { left: 8, right: 8, top: 8, bottom: hasAxisName ? 34 : 22, containLabel: true }
      : showAxis
        ? { left: 8, right: 8, top: 8, bottom: hasAxisName ? 34 : 22, containLabel: true }
        : { left: 8, right: 8, top: 8, bottom: 8 },
    xAxis: {
      type: 'category',
      show: showAxis,
      ...xAxisNameExt,
      data: dedupedLabels,
      boundaryGap: false,
      axisLabel: {
        fontSize: 9,
        color: '#6b7280',
        interval: axisMode === 'date' ? ('auto' as const) : 0,
        hideOverlap: true,
      },
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisTick: { show: false },
    },
    yAxis: yAxisOpt,
    series: [
      {
        type: 'line' as const,
        data: dataPoints,
        smooth: true,
        lineStyle: { color: SPARKLINE_COLOR, width: 2 },
        // Show point markers when we annotate n per point so each cohort is
        // hoverable (quick-260625-nem Task 5); otherwise keep the clean line.
        symbol: counts != null ? 'circle' : 'none',
        symbolSize: 5,
        // quick-260626-cot Task 2.1: vertical gradient fill (denser at the line,
        // fading to transparent at the baseline) so the filler-zone sparkline
        // reads as the mock's ${AREA} area chart, not a thin hairline.
        areaStyle: {
          color: {
            type: 'linear' as const,
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(40,81,230,0.22)' },
              { offset: 1, color: 'rgba(40,81,230,0.0)' },
            ],
          },
        },
        ...markLineExt,
      },
    ],
    // quick-260625-nem Task 5: when per-point sample sizes (counts) are provided,
    // enable an axis tooltip annotating value + n so small-/outlier-driven
    // cohorts are visible. Otherwise the sparkline stays tooltip-free.
    tooltip:
      counts != null
        ? {
            trigger: 'axis' as const,
            backgroundColor: 'rgba(17,24,39,0.9)',
            borderColor: 'transparent',
            textStyle: { color: '#fff', fontSize: 11 },
            formatter: (params: unknown) => {
              const arr = Array.isArray(params) ? params : [params];
              const p = arr[0] as { dataIndex?: number; value?: unknown } | undefined;
              const i = p?.dataIndex ?? 0;
              const val = Number(p?.value ?? 0);
              const n = counts[i];
              const valStr = `${val.toFixed(1)}${valueSuffix ? ` ${valueSuffix}` : ''}`;
              const label = labels[i] ?? '';
              const nStr = n != null ? ` · n = ${String(n)}` : '';
              return `${label}: ${valStr}${nStr}`;
            },
          }
        : { show: false },
  };
}

/** Get a color from the stack palette, always returning a string (noUncheckedIndexedAccess safe). */
function stackColor(i: number): string {
  return CHART_STACK_COLORS[i % CHART_STACK_COLORS.length] ?? CHART_STACK_COLORS[0] ?? '#1f3fb4';
}

function buildStackedBar(input: {
  categories: string[];
  series: { name: string; values: number[] }[];
}): EChartsOption {
  return {
    animation: false,
    grid: { left: 48, right: 12, top: 8, bottom: 44, containLabel: true },
    xAxis: {
      type: 'category',
      data: input.categories,
      axisLabel: {
        fontSize: 10,
        color: '#6b7280',
        interval: 0,
        rotate: input.categories.length > 4 ? 25 : 0,
        hideOverlap: true,
      },
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      show: true,
      // Thousands-separated integer count ticks (quick-260626-j5u Task 1).
      axisLabel: { fontSize: 10, color: '#6b7280', formatter: countTick },
      splitLine: { show: true, lineStyle: { color: '#f3f4f6' } },
    },
    series: input.series.map((s, i) => ({
      type: 'bar' as const,
      name: s.name,
      data: s.values,
      stack: 'total',
      itemStyle: { color: stackColor(i) },
    })),
    legend: { show: false },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(17,24,39,0.9)',
      borderColor: 'transparent',
      textStyle: { color: '#fff', fontSize: 11 },
      valueFormatter: (v) => countTick(Number(v)),
    },
  };
}

/**
 * Build a vertical date-axis bar chart (quick-260626-g28 Task 2). One thin bar
 * per day; the X-axis is the daily 'M/D' labels (auto-thinned via interval +
 * hideOverlap so a 30-day window stays readable). Weekend / no-activity days
 * render as low or zero bars so the weekday rhythm is visible. Single series.
 */
function buildDailyBar(
  input: {
    categories: string[];
    series: { name: string; values: number[] }[];
  },
  target?: number,
): EChartsOption {
  const values = input.series[0]?.values ?? [];
  // quick-260630-f7f Task 1 (item 3): optional horizontal target markLine on the
  // single bar series (e.g. MAIN-01 New Meetings Booked weekly goal = 120/week —
  // quick-260630-wiw).
  const markLineExt =
    target != null && target > 0
      ? {
          markLine: {
            silent: true,
            symbol: 'none' as const,
            lineStyle: {
              color: '#d97706',
              width: 1,
              type: 'dashed' as const,
            },
            data: [{ yAxis: target, name: 'target' }],
            label: {
              show: true,
              position: 'insideEndTop' as const,
              formatter: `Target ${String(Math.round(target))}`,
              fontSize: 9,
              color: '#d97706',
            },
          },
        }
      : {};
  return {
    animation: false,
    grid: { left: 40, right: 12, top: 8, bottom: 28, containLabel: true },
    xAxis: {
      type: 'category',
      data: input.categories,
      axisLabel: {
        fontSize: 9,
        color: '#6b7280',
        interval: 'auto',
        hideOverlap: true,
      },
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      show: true,
      // Thousands-separated integer count ticks (quick-260626-j5u Task 1).
      axisLabel: { fontSize: 10, color: '#6b7280', formatter: countTick },
      splitLine: { show: true, lineStyle: { color: '#f3f4f6' } },
    },
    series: [
      {
        type: 'bar' as const,
        name: input.series[0]?.name ?? 'Count',
        data: values,
        barMaxWidth: 14,
        itemStyle: { color: '#2851e6', borderRadius: [2, 2, 0, 0] },
        ...markLineExt,
      },
    ],
    legend: { show: false },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(17,24,39,0.9)',
      borderColor: 'transparent',
      textStyle: { color: '#fff', fontSize: 11 },
      valueFormatter: (v) => countTick(Number(v)),
    },
  };
}

function buildArea(input: {
  categories: string[];
  series: { name: string; values: number[] }[];
}): EChartsOption {
  return {
    animation: false,
    grid: { left: 48, right: 12, top: 8, bottom: 44, containLabel: true },
    xAxis: {
      type: 'category',
      data: input.categories,
      axisLabel: {
        fontSize: 10,
        color: '#6b7280',
        interval: 0,
        rotate: input.categories.length > 4 ? 25 : 0,
        hideOverlap: true,
      },
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      show: true,
      axisLabel: { fontSize: 10, color: '#6b7280' },
      splitLine: { show: true, lineStyle: { color: '#f3f4f6' } },
    },
    series: input.series.map((s, i) => ({
      type: 'line' as const,
      name: s.name,
      data: s.values,
      smooth: true,
      lineStyle: { color: stackColor(i), width: 2 },
      areaStyle: {
        color: stackColor(i),
        opacity: 0.15,
      },
      symbol: 'none',
    })),
    legend: { show: false },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(17,24,39,0.9)',
      borderColor: 'transparent',
      textStyle: { color: '#fff', fontSize: 11 },
    },
  };
}

/**
 * Thousands-separated integer Y-axis/label formatter for COUNT axes
 * (quick-260626-j5u Task 1). Count charts (daily/weekly activity, stacked-bar
 * volume) plot whole-number counts; the axis ticks + tooltips must read
 * "2,740" / "11,900", never "2740.5". Rounds defensively + groups thousands.
 */
function countTick(v: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(
    Math.round(v),
  );
}

/** Compact Y-axis tick formatter for currency values ($Nk / $N.NM). */
function currencyTick(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (abs >= 1000) return `$${String(Math.round(v / 1000))}k`;
  return `$${String(Math.round(v))}`;
}

function buildLineByQuarter(
  input: {
    categories: string[];
    // quick-260630-icb Task 3: `null` values render as line gaps (e.g. the
    // MAIN-12 closed-won line at quarters with no closed-won deals).
    series: { name: string; values: (number | null)[] }[];
  },
  opts?: LineByQuarterOptions,
): EChartsOption {
  const valueUnit = opts?.valueUnit ?? 'number';

  // Y-axis tick + tooltip value formatter per unit (quick-260625-nem Tasks 2/4):
  // 'days' → "243 days", 'currency' → "$49k", else the raw rounded number.
  const yTickFormatter = (v: number): string => {
    if (valueUnit === 'currency') return currencyTick(v);
    if (valueUnit === 'days') return String(Math.round(v));
    // 'percent' values are stored 0..1 (RESEARCH §10) — render × 100 (MAIN-49
    // Marketing-Sourced Win Rate).
    if (valueUnit === 'percent') return `${String(Math.round(v * 100))}%`;
    return String(Math.round(v));
  };
  const valueLabel = (v: number): string => {
    if (valueUnit === 'currency') return currencyTick(v);
    if (valueUnit === 'days') return `${v.toFixed(1)} days`;
    if (valueUnit === 'percent') return `${(v * 100).toFixed(1)}%`;
    return v.toFixed(1);
  };

  return {
    animation: false,
    grid: { left: 56, right: 12, top: 16, bottom: 48, containLabel: true },
    xAxis: {
      type: 'category',
      name: 'Quarter (FY)',
      nameLocation: 'middle',
      nameGap: 32,
      nameTextStyle: { fontSize: 10, color: '#6b7280' },
      data: input.categories,
      axisLabel: {
        fontSize: 10,
        color: '#6b7280',
        interval: 0,
        rotate: input.categories.length > 4 ? 25 : 0,
        hideOverlap: true,
      },
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      show: true,
      name: opts?.yAxisName ?? '',
      nameLocation: 'middle',
      nameGap: 44,
      nameTextStyle: { fontSize: 10, color: '#6b7280' },
      axisLabel: { fontSize: 10, color: '#6b7280', formatter: yTickFormatter },
      splitLine: { show: true, lineStyle: { color: '#f3f4f6' } },
    },
    series: input.series.map((s, i) => ({
      type: 'line' as const,
      name: s.name,
      data: s.values,
      smooth: true,
      lineStyle: { color: stackColor(i), width: 2 },
      symbol: 'circle',
      symbolSize: 7,
      itemStyle: { color: stackColor(i) },
      label: {
        show: true,
        position: 'top',
        fontSize: 9,
        color: '#6b7280',
        // quick-260630-icb Task 3: null points are line GAPS — no '$0' label.
        formatter: (p: { value?: unknown }) =>
          p.value == null ? '' : valueLabel(Number(p.value)),
      },
    })),
    legend: { show: false },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(17,24,39,0.9)',
      borderColor: 'transparent',
      textStyle: { color: '#fff', fontSize: 11 },
      valueFormatter: (v) => valueLabel(Number(v)),
    },
  };
}

/**
 * Build a horizontal-bar chart (quick-260626-cot Task 3.3). One bar per
 * category on the Y-axis; the caller pre-sorts categories/values descending so
 * the longest bar sits on top. Value labels + X-axis ticks format per
 * `opts.valueUnit`.
 */
function buildHorizontalBar(
  input: {
    categories: string[];
    series: { name: string; values: number[] }[];
  },
  opts?: HorizontalBarOptions,
): EChartsOption {
  const valueUnit = opts?.valueUnit ?? 'number';

  const tickFormatter = (v: number): string => {
    if (valueUnit === 'percent') return `${String(Math.round(v * 100))}%`;
    if (valueUnit === 'currency') return currencyTick(v);
    if (valueUnit === 'days') return String(Math.round(v));
    return String(Math.round(v));
  };
  const valueLabel = (v: number): string => {
    if (valueUnit === 'percent') return `${(v * 100).toFixed(1)}%`;
    if (valueUnit === 'currency') return currencyTick(v);
    if (valueUnit === 'days') return `${v.toFixed(1)} days`;
    return v.toFixed(0);
  };

  const values = input.series[0]?.values ?? [];

  // Taller charts get a little more left gutter for long channel names; ECharts
  // containLabel handles the rest. inverse Y so the first (largest) bar is top.
  return {
    animation: false,
    grid: { left: 8, right: 56, top: 8, bottom: 8, containLabel: true },
    xAxis: {
      type: 'value',
      show: true,
      axisLabel: { fontSize: 10, color: '#6b7280', formatter: tickFormatter },
      splitLine: { show: true, lineStyle: { color: '#f3f4f6' } },
    },
    yAxis: {
      type: 'category',
      inverse: true,
      data: input.categories,
      axisLabel: { fontSize: 11, color: '#374251', hideOverlap: true },
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisTick: { show: false },
    },
    series: [
      {
        type: 'bar' as const,
        data: values,
        barMaxWidth: 22,
        itemStyle: { color: '#1AA7C8', borderRadius: [0, 4, 4, 0] },
        label: {
          show: true,
          position: 'right',
          fontSize: 10,
          color: '#4E5A6B',
          formatter: (p: { value?: unknown }) => valueLabel(Number(p.value ?? 0)),
        },
      },
    ],
    legend: { show: false },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(17,24,39,0.9)',
      borderColor: 'transparent',
      textStyle: { color: '#fff', fontSize: 11 },
      valueFormatter: (v) => valueLabel(Number(v)),
    },
  };
}

// Pacing-chart palette (matches the mock's .pace-legend treatment).
const PACE_ACTUAL = '#1AA7C8'; // cyan cumulative-actual line
const PACE_TARGET = '#4E5A6B'; // ink-600 dashed target pace line
const PACE_GAP = 'rgba(240,162,48,0.28)'; // amber shaded gap

/**
 * Build the cumulative-vs-target pacing chart (quick-260626-cot Task 3.2).
 *
 * Three series over a fiscal-month X-axis:
 *   1. Target pace — a straight line from 0 (first month) → target (last month),
 *      rendered dashed. Linearly interpolated across the month span.
 *   2. Cumulative actual — cyan line up to "now" (cumulative.length points).
 *   3. Gap band — amber area = (targetAtMonth − actual) STACKED on the actual
 *      line, so the shaded region sits between actual and target wherever actual
 *      is below target. Only spans the months that have an actual point.
 *
 * The gap series is stacked on a hidden copy of the actual line so ECharts fills
 * the vertical band between the two without a custom renderer.
 */
function buildPacing(input: PacingInput): EChartsOption {
  const months = input.months;
  const n = months.length;
  const target = input.target > 0 ? input.target : 0;

  // Straight target pace: 0 at month 0 → target at the last month.
  const targetLine: number[] = months.map((_, i) =>
    n > 1 ? (target * i) / (n - 1) : target,
  );

  // Cumulative actual, clamped to the month span. null past "now" so the line
  // stops at the latest actual point (no phantom drop to 0).
  const actual: (number | null)[] = months.map((_, i) =>
    i < input.cumulative.length ? (input.cumulative[i] ?? 0) : null,
  );

  // Gap = max(0, targetAtMonth − actual) for months that HAVE an actual point;
  // stacked on the actual line to shade the band between actual and target.
  const actualBase: (number | null)[] = actual.map((v) => v ?? null);
  const gap: (number | null)[] = actual.map((v, i) =>
    v == null ? null : Math.max(0, (targetLine[i] ?? 0) - v),
  );

  return {
    animation: false,
    grid: { left: 8, right: 8, top: 10, bottom: 24, containLabel: true },
    xAxis: {
      type: 'category',
      data: months,
      boundaryGap: false,
      axisLabel: {
        fontSize: 9,
        color: '#94A1B0',
        interval: 'auto',
        hideOverlap: true,
      },
      axisLine: { lineStyle: { color: '#E4E9EF' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      show: true,
      min: 0,
      axisLabel: { fontSize: 9, color: '#94A1B0', formatter: currencyTick },
      splitLine: { show: true, lineStyle: { color: '#EEF2F6' } },
    },
    series: [
      // Hidden base = actual line; the gap area stacks on top of it.
      {
        type: 'line' as const,
        name: '_base',
        data: actualBase,
        stack: 'gap',
        symbol: 'none',
        lineStyle: { opacity: 0 },
        areaStyle: { opacity: 0 },
        silent: true,
        z: 1,
      },
      // Amber gap band between actual and target.
      {
        type: 'line' as const,
        name: 'Gap to target',
        data: gap,
        stack: 'gap',
        symbol: 'none',
        lineStyle: { opacity: 0 },
        areaStyle: { color: PACE_GAP },
        silent: true,
        z: 2,
      },
      // Straight dashed target pace line.
      {
        type: 'line' as const,
        name: 'Target pace',
        data: targetLine,
        symbol: 'none',
        lineStyle: { color: PACE_TARGET, width: 1.5, type: 'dashed' },
        z: 3,
      },
      // Cumulative actual line (cyan), end-capped with a marker dot.
      {
        type: 'line' as const,
        name: 'Cumulative actual',
        data: actual,
        smooth: false,
        connectNulls: false,
        symbol: 'circle',
        symbolSize: 5,
        showSymbol: false,
        lineStyle: { color: PACE_ACTUAL, width: 2.5 },
        itemStyle: { color: PACE_ACTUAL },
        z: 4,
      },
    ],
    legend: { show: false },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(17,24,39,0.9)',
      borderColor: 'transparent',
      textStyle: { color: '#fff', fontSize: 11 },
      formatter: (params: unknown) => {
        const arr = Array.isArray(params) ? params : [params];
        const first = arr[0] as { axisValue?: string } | undefined;
        const label = first?.axisValue ?? '';
        // Surface only the actual + target lines (skip the hidden base + gap).
        const lines: string[] = [];
        for (const p of arr as { seriesName?: string; value?: unknown }[]) {
          if (p.seriesName === 'Cumulative actual' || p.seriesName === 'Target pace') {
            const v = Number(p.value ?? 0);
            if (Number.isFinite(v)) lines.push(`${p.seriesName}: ${currencyTick(v)}`);
          }
        }
        return `${label}<br/>${lines.join('<br/>')}`;
      },
    },
  };
}
